import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useSettings } from "../context/SettingsContext";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const subcategory = params.get("subcategory") || "";
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const { categories } = useSettings();

  const activeCat = categories.find((c) => c.key === category);

  useEffect(() => {
    setLoading(true);
    const qp = new URLSearchParams();
    if (category !== "all") qp.set("category", category);
    if (subcategory) qp.set("subcategory", subcategory);
    const qs = qp.toString() ? `?${qp.toString()}` : "";
    api
      .get(`/products${qs}`)
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, subcategory]);

  const setCategory = (key) => {
    const next = new URLSearchParams(params);
    next.delete("subcategory");
    if (key === "all") next.delete("category");
    else next.set("category", key);
    setParams(next);
  };

  const setSub = (key) => {
    const next = new URLSearchParams(params);
    if (subcategory === key) next.delete("subcategory");
    else next.set("subcategory", key);
    setParams(next);
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A231C] font-display">Shop</h1>
      <p className="text-[#5C665D] mt-2">Browse everything available in our store.</p>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mt-8">
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="filter-all"
            onClick={() => setCategory("all")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              category === "all" ? "bg-[var(--brand-primary)] text-white" : "bg-[#F4EFE6] text-[#1A231C] hover:bg-[#E8E4D9]"
            }`}
          >
            All
          </button>
          {categories.filter((c) => (c.depth || 0) === 0).map((c) => (
            <button
              key={c.key}
              data-testid={`filter-${c.key}`}
              onClick={() => setCategory(c.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                category === c.key ? "bg-[var(--brand-primary)] text-white" : "bg-[#F4EFE6] text-[#1A231C] hover:bg-[#E8E4D9]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="relative w-full lg:w-72">
          <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="product-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products..."
            className="w-full bg-white border border-[#E8E4D9] rounded-lg pl-9 pr-4 py-2.5 text-[#1A231C] placeholder-[#9CA3AF] focus:outline-none focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary)]"
          />
        </div>
      </div>

      {activeCat && (activeCat.subcategories || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {activeCat.subcategories.map((s) => (
            <button
              key={s.key}
              data-testid={`subfilter-${s.key}`}
              onClick={() => setSub(s.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                subcategory === s.key
                  ? "bg-[var(--brand-accent)] text-white border-[var(--brand-accent)]"
                  : "bg-white text-[#5C665D] border-[#E8E4D9] hover:border-[var(--brand-accent)]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white border border-[#E8E4D9] rounded-xl h-80 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-[#5C665D] mt-16 text-center" data-testid="no-products">
          No products found.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {filtered.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}
